/**
 * @fileoverview Sudoku Game Types
 * @description Type definitions for the Sudoku game module
 */

/** Difficulty levels */
export type SudokuDifficulty = "easy" | "medium" | "hard";

/** Cell value: 1-9 or null (empty) */
export type CellValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | null;

/** Cell status for styling */
export type CellStatus =
  | "given" // Pre-filled, cannot edit
  | "user" // User entered
  | "error" // User entered wrong value
  | "hint" // Revealed by hint
  | "selected" // Currently selected
  | "highlighted"; // Same row/col/box or same number

/** A single cell in the grid */
export interface SudokuCell {
  value: CellValue;
  status: CellStatus;
  isGiven: boolean;
  notes: number[]; // Pencil marks (1-9)
  row: number;
  col: number;
}

/** 9x9 grid type (flat array of 81 cells) */
export type SudokuGrid = SudokuCell[];

/** Puzzle from database */
export interface SudokuPuzzle {
  id: string;
  difficulty: SudokuDifficulty;
  puzzle: string; // 81 chars
  solution: string; // 81 chars
  name: string | null;
  clues_count: number | null;
  is_active: boolean;
  created_at: string;
}

/** Game state stored in game_sessions.game_state */
export interface SudokuGameState {
  puzzle_id: string;
  difficulty: SudokuDifficulty;
  puzzle: string; // Original puzzle (81 chars)
  solution: string; // Solution (81 chars)
  current: string; // Current user state (81 chars)
  notes: Record<number, number[]>; // Cell index -> notes array
  moves: SudokuMove[];
  mistakes: number;
  hints_used: number;
  selected_cell: number | null; // 0-80 index
  is_notes_mode: boolean;
  started_at: string;
  completed_at: string | null;
  is_completed: boolean;
  is_failed: boolean;
  time_elapsed_seconds: number;
}

/** A single move for undo functionality */
export interface SudokuMove {
  cell_index: number;
  previous_value: CellValue;
  new_value: CellValue;
  previous_notes: number[];
  new_notes: number[];
  is_note_move: boolean;
  timestamp: string;
}

/** Game result for stats and completion */
export interface SudokuResult {
  completed: boolean;
  failed: boolean;
  time_seconds: number;
  mistakes: number;
  hints_used: number;
  is_perfect: boolean; // No mistakes, no hints
  difficulty: SudokuDifficulty;
  points_earned: number;
}

/** Difficulty configuration */
export interface DifficultyConfig {
  difficulty: SudokuDifficulty;
  label: string;
  description: string;
  color: string;
  clues_range: [number, number];
  max_hints: number;
  max_mistakes: number;
  points: number;
}

/** All difficulty configurations */
export const DIFFICULTY_CONFIGS: Record<SudokuDifficulty, DifficultyConfig> = {
  easy: {
    difficulty: "easy",
    label: "Makkelijk",
    description: "35-40 cijfers zichtbaar",
    color: "#10B981",
    clues_range: [35, 40],
    max_hints: 5,
    max_mistakes: 5,
    points: 30,
  },
  medium: {
    difficulty: "medium",
    label: "Normaal",
    description: "28-34 cijfers zichtbaar",
    color: "#F59E0B",
    clues_range: [28, 34],
    max_hints: 3,
    max_mistakes: 3,
    points: 50,
  },
  hard: {
    difficulty: "hard",
    label: "Moeilijk",
    description: "22-27 cijfers zichtbaar",
    color: "#EF4444",
    clues_range: [22, 27],
    max_hints: 2,
    max_mistakes: 2,
    points: 100,
  },
};

/** Props for the Sudoku board component */
export interface SudokuBoardProps {
  grid: SudokuGrid;
  selectedCell: number | null;
  onCellPress: (index: number) => void;
  disabled?: boolean;
}

/** Props for a single cell */
export interface SudokuCellProps {
  cell: SudokuCell;
  index: number;
  isSelected: boolean;
  isHighlighted: boolean;
  isSameNumber: boolean;
  onPress: () => void;
  disabled?: boolean;
  cellSize: number;
}

/** Props for the number pad */
export interface NumberPadProps {
  onNumberPress: (num: number) => void;
  onClearPress: () => void;
  onUndoPress: () => void;
  onHintPress: () => void;
  onNotesToggle: () => void;
  isNotesMode: boolean;
  hintsRemaining: number;
  disabledNumbers?: number[];
  disabled?: boolean;
}

/** Weekend config for Sudoku (stored in weekend_game_config.custom_config) */
export interface SudokuWeekendConfig {
  max_plays_per_day: number;
  allow_unlimited_plays: boolean;
  points_per_difficulty: {
    easy: number;
    medium: number;
    hard: number;
  };
  perfect_bonus: number;
}

/** Default weekend config */
export const DEFAULT_WEEKEND_CONFIG: SudokuWeekendConfig = {
  max_plays_per_day: 3,
  allow_unlimited_plays: false,
  points_per_difficulty: {
    easy: 30,
    medium: 50,
    hard: 100,
  },
  perfect_bonus: 10,
};

/** User stats for Sudoku (stored in user_game_stats.stats) */
export interface SudokuUserStats {
  easy: {
    games_played: number;
    games_won: number;
    best_time_seconds: number | null;
    total_time_seconds: number;
    avg_mistakes: number;
    perfect_games: number;
  };
  medium: {
    games_played: number;
    games_won: number;
    best_time_seconds: number | null;
    total_time_seconds: number;
    avg_mistakes: number;
    perfect_games: number;
  };
  hard: {
    games_played: number;
    games_won: number;
    best_time_seconds: number | null;
    total_time_seconds: number;
    avg_mistakes: number;
    perfect_games: number;
  };
  total_hints_used: number;
  total_mistakes: number;
}

/** Initial empty stats */
export const EMPTY_SUDOKU_STATS: SudokuUserStats = {
  easy: {
    games_played: 0,
    games_won: 0,
    best_time_seconds: null,
    total_time_seconds: 0,
    avg_mistakes: 0,
    perfect_games: 0,
  },
  medium: {
    games_played: 0,
    games_won: 0,
    best_time_seconds: null,
    total_time_seconds: 0,
    avg_mistakes: 0,
    perfect_games: 0,
  },
  hard: {
    games_played: 0,
    games_won: 0,
    best_time_seconds: null,
    total_time_seconds: 0,
    avg_mistakes: 0,
    perfect_games: 0,
  },
  total_hints_used: 0,
  total_mistakes: 0,
};

